import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { NotificationDto } from "../../models/notifications/notification.interface";
import { WHITE_LABEL_APP_ID, WhiteLabelAppId } from "../../providers/white-label-theme.provider";
import { resolveNotificationTarget } from "../../utils/notification-target.util";
import { resolveAppUrl } from "../../utils/app-url.util";
import { ProductConfigService } from "../products/product-config.service";

/**
 * Abre o destino da ação contextual de uma notificação (DEV-895, CA 01 e CA 02).
 *
 * Existe como serviço, e não dentro dos containers, porque a MESMA decisão é necessária no painel e
 * na toaster — duplicá-la nos dois seria concentrar regra compartilhada em componente.
 *
 * A decisão é uma só: se a tela mora no app corrente, navega pelo `Router`; se mora no outro app, é
 * salto de topo. O precedente do salto com path é `impersonation.service.ts:437`
 * (`${adminUrl()}/users-manager`), e as URLs de `ProductConfigService` são relativas (`/app`,
 * `/admin`) para resolverem same-origin atrás do proxy de domínio único.
 */
@Injectable({ providedIn: "root" })
export class NotificationNavigationService {
  private readonly router = inject(Router);
  private readonly productConfig = inject(ProductConfigService);
  /**
   * Mesmo token que o `FilterStateService` injeta para namespacing de storage. Provido por
   * `provideWhiteLabelTheme` no `app.config.ts` de todos os apps, então é injeção obrigatória: um
   * shell sem ele é erro de configuração, não estado a tolerar em silêncio.
   */
  private readonly currentApp = inject(WHITE_LABEL_APP_ID);

  /**
   * `false` = não havia para onde ir (tipo sem ação, payload sem o id, ou app de destino sem URL
   * configurada). Quem chama usa isso para decidir se fecha o painel — fechar sem navegar deixaria
   * a pessoa numa tela que ela não pediu para trocar.
   */
  public open(notification: NotificationDto): boolean {
    const target = resolveNotificationTarget(notification);

    if (!target) {
      return false;
    }

    if (target.app === this.currentApp) {
      // Cópia mutável: `Router.navigate` recebe `unknown[]`, e `commands` é `readonly`.
      void this.router.navigate([...target.commands]);
      return true;
    }

    const baseUrl = this.resolveAppBaseUrl(target.app);

    if (!baseUrl) {
      return false;
    }

    this.redirectTo(`${baseUrl}/${target.path}`);
    return true;
  }

  /**
   * O `null` do login é regra DESTE serviço, e por isso fica aqui e não no utilitário: o login não
   * hospeda tela de destino, e `ProductConfig.apps` não declara URL para ele. O que é compartilhado
   * — a raiz de admin e de plataforma — sai de `resolveAppUrl`, para não manter uma sétima cópia do
   * mesmo par de chamadas.
   */
  private resolveAppBaseUrl(app: WhiteLabelAppId): string | null {
    return app === "login" ? null : resolveAppUrl(app, this.productConfig);
  }

  /**
   * Seam de navegação: jsdom não permite atribuição real de `location.href`, então os testes
   * substituem este método. Mesmo padrão de `impersonation.service.ts:redirectTo`.
   */
  private redirectTo(url: string): void {
    globalThis.location.href = url;
  }
}
